'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Question as QuestionIcon } from '@phosphor-icons/react/dist/ssr/Question';
import { useLocation } from 'react-router-dom';

import { paths } from '@/paths';
import { getArticleBySlug } from '@/pages/help/data/help-articles';
import { useHelpAccess } from '@/pages/help/use-help-access';
import { HelpDialog } from '@/pages/help/components/help-dialog';

interface HelpRouteHint {
  /** Pathname prefix to match (longest match wins). */
  prefix: string;
  /** Human-readable subject used in the tooltip, e.g. "users". */
  subject: string;
  /** Optional slug to deep-link to a specific guide; otherwise the help index. */
  slug?: string;
}

/**
 * Maps the current page to the most relevant how-to guide so we can surface a
 * contextual "need help?" entry point in the top bar on every page.
 */
const HELP_ROUTE_HINTS: HelpRouteHint[] = [
  { prefix: '/users', subject: 'users', slug: 'add-a-user' },
  { prefix: '/units', subject: 'units', slug: 'add-a-unit' },
  { prefix: '/reservations', subject: 'reservations', slug: 'create-a-reservation' },
  { prefix: '/calendar-events', subject: 'the calendar', slug: 'create-a-reservation' },
  { prefix: '/announcements', subject: 'announcements', slug: 'create-an-announcement' },
  { prefix: '/requests', subject: 'requests', slug: 'submit-a-request' },
  { prefix: '/documents', subject: 'documents', slug: 'upload-a-document' },
  { prefix: '/meetings', subject: 'meetings', slug: 'schedule-a-meeting' },
  { prefix: '/settings/community/amenities', subject: 'amenity setup', slug: 'set-up-an-amenity' },
  { prefix: '/settings/community/payments', subject: 'payments', slug: 'connect-stripe' },
  { prefix: '/settings/community/preferences', subject: 'community settings', slug: 'configure-community-preferences' },
  { prefix: '/settings/details/payments', subject: 'payments', slug: 'connect-stripe' },
  { prefix: '/settings/account/administrators', subject: 'administrators', slug: 'invite-an-administrator' },
  { prefix: '/settings/account/communities', subject: 'communities', slug: 'create-a-community' },
];

function getHelpHint(pathname: string): HelpRouteHint | undefined {
  return HELP_ROUTE_HINTS.filter((hint) => pathname === hint.prefix || pathname.startsWith(`${hint.prefix}/`)).sort(
    (a, b) => b.prefix.length - a.prefix.length
  )[0];
}

export function ContextualHelpButton(): React.JSX.Element | null {
  const { pathname } = useLocation();
  const { canSeeArticle } = useHelpAccess();
  const [open, setOpen] = React.useState(false);

  // Don't show the entry point while already on the help center.
  if (pathname === paths.help.index || pathname.startsWith(`${paths.help.index}/`)) {
    return null;
  }

  const hint = getHelpHint(pathname);
  const article = hint?.slug ? getArticleBySlug(hint.slug) : undefined;
  const accessible = article ? canSeeArticle(article) : false;
  const slug = article && accessible ? article.slug : undefined;
  const tooltip = hint && accessible ? `Need help with ${hint.subject}?` : 'Browse help guides';

  return (
    <React.Fragment>
      <Tooltip title={tooltip}>
        <Box
          aria-label="Open help"
          onClick={() => setOpen(true)}
          role="button"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            cursor: 'pointer',
            px: 1,
            py: 0.5,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            transition: 'border-color 150ms, background-color 150ms, color 150ms',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover', color: 'primary.main' },
          }}
        >
          <QuestionIcon size={16} weight="bold" />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, display: { xs: 'none', md: 'block' } }}>
            Help
          </Typography>
        </Box>
      </Tooltip>
      <HelpDialog onClose={() => setOpen(false)} open={open} slug={slug} />
    </React.Fragment>
  );
}
